import type { PersonaRequestDto } from '@inference.engine/api-client/web';

import type { Persona } from '../model/types';

interface OptimisticPersonaContext {
  workspaceId: string;
  organizationId: string;
}

/**
 * The workspaces a persona is about to be granted, in the order persona-api resolves them: the
 * author's own always among them, whatever the request named, because the pipeline that fills the
 * persona in writes back as the author and cannot reach one they do not have.
 */
function grantedWorkspaceIds(payload: PersonaRequestDto, workspaceId: string): string[] {
  return [...new Set([workspaceId, ...(payload.workspaceIds ?? [])])];
}

/**
 * Builds a minimal list-row persona for immediate UI feedback after creation returns 202, from
 * either endpoint the runtime flag selects. Replaced on the next persona-api refetch.
 */
export function buildOptimisticProcessingPersona(
  personaId: string,
  payload: PersonaRequestDto,
  context: OptimisticPersonaContext,
): Persona {
  const now = new Date().toISOString();
  const base = {
    id: personaId,
    // eslint-disable-next-line i18next/no-literal-string -- wire enum value the persona API returns
    creationStatus: 'processing' as const,
    segmentName: payload.segmentName,
    segmentDescription: payload.segmentDescription,
    topicOfInterest: payload.topicOrBrandOfInterest?.[0],
    created: now,
    updated: now,
  };

  if (payload.spaceVisibility === 'workspace' || payload.spaceVisibility === 'organization') {
    return {
      ...base,
      type: 'Scoped',
      organizationId: context.organizationId,
      visibility: payload.spaceVisibility === 'organization' ? 'organization' : 'workspaces',
      ...(payload.spaceVisibility === 'workspace'
        ? // Mirrors what persona-api writes to the legacy column: the lowest of the granted ids.
          { workspaceId: grantedWorkspaceIds(payload, context.workspaceId).sort()[0] }
        : {}),
    } as Persona;
  }

  return {
    ...base,
    type: 'Default',
  } as Persona;
}
